/**
 * Awaiting Assignment / Welcome experience helpers.
 *
 * A Workspace Member with active workspace membership but no project-wide
 * assignment and no object-scoped responsibility lands on Welcome only.
 */

import type { User, UserScopedRelationship } from '@/types';
import { getActiveScopedRelationships } from './scopedRelationshipResolver';
import { resolveIdentityRoles } from './identityRoles';

/** Relationships that grant a governed responsibility beyond bare workspace entry. */
const GOVERNED_RESPONSIBILITY_TYPES = new Set([
  'project_manager',
  'project_contributor',
  'observer',
  'validator',
  'reporting_recipient',
  'decision_participant',
  'company_representative',
  'partner_representative',
  'vendor_representative',
  'investor_representative',
  'investment_manager',
]);

export function hasGovernedResponsibility(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
): boolean {
  return getActiveScopedRelationships(user).some((relationship) =>
    GOVERNED_RESPONSIBILITY_TYPES.has(relationship.type),
  );
}

/**
 * True when the principal is an organization_member whose only active scope
 * is workspace membership (or nothing) — i.e. Awaiting Assignment.
 */
export function isAwaitingAssignment(user: User | null | undefined): boolean {
  if (!user) return false;
  const { accountRole, platformRole } = resolveIdentityRoles(user);
  if (platformRole) return false;
  if (accountRole !== 'organization_member') return false;
  return !hasGovernedResponsibility(user);
}

export function getAwaitingAssignmentWelcomeCopy(workspaceName: string): string {
  return (
    `Your access to ${workspaceName} is active. You have not yet been assigned ` +
    'to a project or another governed responsibility. Once an administrator ' +
    'completes your assignment, the relevant tools and information will appear automatically.'
  );
}

/** Active workspace_member relationship, if any. */
export function getActiveWorkspaceMembership(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
  workspaceId?: string,
): UserScopedRelationship | null {
  return (
    getActiveScopedRelationships(user, 'workspace', workspaceId).find(
      (relationship) => relationship.type === 'workspace_member',
    ) ?? null
  );
}
